import type { DoneCriterion, WorkItemDetail } from '@deskworks/shared-types';
import { ListTodo, Plus, X } from 'lucide-react';
import { useState } from 'react';
import { RelativeTime } from '@/components/relative-time';
import { Section } from '@/components/section';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import { Input } from '@/components/ui/input';
import { api } from '@/lib/api';
import { useApiMutation } from '@/lib/hooks';
import { t } from '@/lib/i18n';
import { qk } from '@/lib/query-keys';

/**
 * The item's definition of done (docs/features/work-items.md): its kind of work's points plus its own. The Lead checks
 * each off with evidence before it closes the item; a person can tick one off ("the customer looked it over"), open it
 * again or add a point for this item.
 */
export function DoneCriteriaSection({ item }: { item: WorkItemDetail }) {
  const [text, setText] = useState('');
  const [adding, setAdding] = useState(false);
  const invalidate = [qk.workItem(item.id)];
  const check = useApiMutation((c: DoneCriterion) => api.workItems.checkDone(item.id, c.id, { done: !c.done }), { invalidate });
  const add = useApiMutation(() => api.workItems.addDone(item.id, text), {
    invalidate,
    onSuccess: () => {
      setText('');
      setAdding(false);
    },
  });
  const remove = useApiMutation((c: DoneCriterion) => api.workItems.removeDone(item.id, c.id), { invalidate });
  if (item.kind !== 'task' || (!item.doneCriteria.length && !item.typeId)) return null;
  const open = item.doneCriteria.filter((c) => !c.done).length;

  return (
    <Section
      icon={<ListTodo />}
      title={t('Definition of done')}
      description={
        item.doneCriteria.length
          ? open
            ? t('{open} of {total} open — the Lead closes the item only when every point is met.', { open, total: item.doneCriteria.length })
            : t('Every point is met.')
          : t('Its kind of work has no definition of done yet.')
      }
      actions={
        !adding && (
          <Button variant="ghost" size="xs" onClick={() => setAdding(true)} title={t('A point for this item only, e.g. “online before the trade fair”')}>
            <Plus /> {t('Add')}
          </Button>
        )
      }
    >
      <ul className="flex flex-col gap-2 text-sm">
        {item.doneCriteria.map((c) => (
          <li key={c.id} className="group flex items-start gap-2.5">
            <Checkbox className="mt-0.5" checked={c.done} disabled={check.isPending} onChange={() => check.mutate(c)} aria-label={c.text} />
            <div className="min-w-0 flex-1">
              <div className={c.done ? 'text-muted-foreground line-through decoration-muted-foreground/50' : ''}>
                {c.text}
                {c.source === 'item' && <span className="ml-1.5 text-xs text-muted-foreground no-underline">{t('(this item)')}</span>}
              </div>
              {c.done && (
                <div className="text-xs text-muted-foreground">
                  {c.evidence}
                  {c.checkedBy?.name ? ` · ${c.checkedBy.name}` : c.checkedBy?.type === 'agent' ? ` · ${t('checked by an agent')}` : ''}
                  {c.checkedAt && (
                    <>
                      {' · '}
                      <RelativeTime value={c.checkedAt} />
                    </>
                  )}
                </div>
              )}
            </div>
            {c.source === 'item' && (
              <Button variant="ghost" size="icon-sm" className="opacity-0 group-hover:opacity-100" aria-label={t('Remove {text}', { text: c.text })} onClick={() => remove.mutate(c)}>
                <X />
              </Button>
            )}
          </li>
        ))}
      </ul>
      {adding && (
        <form
          className="mt-3 flex gap-2"
          onSubmit={(e) => {
            e.preventDefault();
            if (text.trim()) add.mutate();
          }}
        >
          <Input value={text} onChange={(e) => setText(e.target.value)} placeholder={t('e.g. The customer looks it over before it goes live')} autoFocus />
          <Button size="sm" type="submit" disabled={!text.trim()} loading={add.isPending}>
            {t('Add')}
          </Button>
          <Button size="sm" variant="outline" type="button" onClick={() => setAdding(false)}>
            {t('Cancel')}
          </Button>
        </form>
      )}
    </Section>
  );
}
